import { sampleTerrainRadius, heightAt } from '@/world/terrain'
import {
  MobType, AnimalState, MOB_SPEEDS, MOB_HOME_RANGE,
  FLEE_TRIGGER_DIST, FLEE_DURATION,
  WOLF_HUNT_TRIGGER, WOLF_HUNT_GIVE_UP, WOLF_HUNT_SPEED,
} from './types'
import type { MobData, VillageSimData } from './types'
import { SphericalSpatialHash } from './spatialHash'
import { applyFlocking } from './flocking'
import { updateVillager } from './villagerAI'

const RADIUS = 10_000
const AI_TICK_INTERVAL = 0.25     // 4 Hz
const SPATIAL_REBUILD_INTERVAL = 0.5  // 2 Hz
const HEIGHT_STAGGER = 4          // sample 1/4 of mobs per frame
const SUN_CYCLE_DURATION = 300    // seconds per day cycle

// ─── Fast deterministic hash ────────────────────────────────────────
function quickRng(a: number, b: number): number {
  let h = (a * 2654435761 + b * 340573321) | 0
  h = Math.imul(h ^ (h >>> 16), 2246822507)
  h = Math.imul(h ^ (h >>> 13), 3266489909)
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296
}

// ─── Tangent frame ──────────────────────────────────────────────────
function getTangentFrame(nx: number, ny: number, nz: number) {
  let ex: number, ey: number, ez: number
  if (Math.abs(ny) < 0.9) {
    ex = -nz; ey = 0; ez = nx
  } else {
    ex = 0; ey = nz; ez = -ny
  }
  const el = Math.sqrt(ex * ex + ey * ey + ez * ez)
  ex /= el; ey /= el; ez /= el
  const fx = ny * ez - nz * ey
  const fy = nz * ex - nx * ez
  const fz = nx * ey - ny * ex
  return { ex, ey, ez, fx, fy, fz }
}

// ─── Lerp angle (handle wrapping) ───────────────────────────────────
function lerpAngle(from: number, to: number, t: number): number {
  let diff = to - from
  while (diff > Math.PI) diff -= Math.PI * 2
  while (diff < -Math.PI) diff += Math.PI * 2
  return from + diff * t
}

// ─── Angular distance ───────────────────────────────────────────────
function angularDist(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
): number {
  const dot = ax * bx + ay * by + az * bz
  return Math.acos(Math.min(1, Math.max(-1, dot)))
}

// ─── Animal AI tick ─────────────────────────────────────────────────
function animalAiTick(
  mob: MobData,
  aiDt: number,
  tickSeed: number,
  allMobs: MobData[],
  spatialHash: SphericalSpatialHash,
  cameraNx: number, cameraNy: number, cameraNz: number,
) {
  mob.stateTimer -= aiDt

  const speeds = MOB_SPEEDS[mob.type]
  const homeRange = MOB_HOME_RANGE[mob.type]

  // Skip AI for mounted horses (controlled by player)
  if (mob.mounted) {
    mob.speed = 0
    mob.animSpeed = 0
    return
  }

  const playerDist = angularDist(mob.nx, mob.ny, mob.nz, cameraNx, cameraNy, cameraNz)

  if (mob.type === MobType.Wolf) {
    // Wolves hunt toward the player instead of fleeing
    if (mob.state !== AnimalState.Hunt && playerDist < WOLF_HUNT_TRIGGER) {
      mob.state = AnimalState.Hunt
      mob.stateTimer = 8
    }
  } else {
    // Other animals flee from player proximity
    if (mob.state !== AnimalState.Flee && mob.state !== AnimalState.Hunt && playerDist < FLEE_TRIGGER_DIST) {
      mob.state = AnimalState.Flee
      mob.stateTimer = FLEE_DURATION
      // Flee direction: away from camera
      const { ex, ey, ez, fx, fy, fz } = getTangentFrame(mob.nx, mob.ny, mob.nz)
      const dx = mob.nx - cameraNx
      const dy = mob.ny - cameraNy
      const dz = mob.nz - cameraNz
      const projE = dx * ex + dy * ey + dz * ez
      const projF = dx * fx + dy * fy + dz * fz
      mob.wanderAngle = Math.atan2(projE, projF)
      mob.heading = mob.wanderAngle
    }
  }

  switch (mob.state) {
    case AnimalState.Idle: {
      mob.speed = 0
      mob.animSpeed = 0
      if (mob.stateTimer <= 0) {
        const r = quickRng(tickSeed + 1, mob.homeNx * 91737 + mob.ny * 47291)
        if (r < 0.55) {
          mob.state = AnimalState.Wander
          mob.stateTimer = 2 + quickRng(tickSeed + 2, mob.nz * 33721) * 5
        } else if (r < 0.85) {
          mob.state = AnimalState.Eat
          mob.stateTimer = 1.5 + quickRng(tickSeed + 3, mob.nx * 55123) * 3
        } else {
          mob.stateTimer = 1 + quickRng(tickSeed + 4, mob.ny * 77391) * 2
        }
      }
      break
    }

    case AnimalState.Wander: {
      mob.speed = speeds.wander
      mob.animSpeed = 1

      // Random drift
      const drift = (quickRng(tickSeed + 5, mob.nx * 12345 + mob.nz * 67890) - 0.5) * 1.5
      mob.wanderAngle += drift * aiDt

      // Flocking
      applyFlocking(mob, allMobs, spatialHash)

      // Homing bias — sheep use tighter threshold + stronger pull to stay in pasture
      const distFromHome = angularDist(
        mob.nx, mob.ny, mob.nz,
        mob.homeNx, mob.homeNy, mob.homeNz,
      )
      const homeThreshold = mob.type === MobType.Sheep ? 0.35 : 0.6
      const homeLerp = mob.type === MobType.Sheep ? 0.7 : 0.3
      if (distFromHome > homeRange * homeThreshold) {
        const { ex, ey, ez, fx, fy, fz } = getTangentFrame(mob.nx, mob.ny, mob.nz)
        const toHomeX = mob.homeNx - mob.nx
        const toHomeY = mob.homeNy - mob.ny
        const toHomeZ = mob.homeNz - mob.nz
        const projE = toHomeX * ex + toHomeY * ey + toHomeZ * ez
        const projF = toHomeX * fx + toHomeY * fy + toHomeZ * fz
        const homeAngle = Math.atan2(projE, projF)
        const strength = Math.min(1, (distFromHome - homeRange * homeThreshold) / (homeRange * (1 - homeThreshold)))
        mob.wanderAngle = lerpAngle(mob.wanderAngle, homeAngle, strength * homeLerp)
      }

      // Water avoidance
      {
        const { ex, ey, ez, fx, fy, fz } = getTangentFrame(mob.nx, mob.ny, mob.nz)
        const lookAhead = 5 / RADIUS
        const cosH = Math.cos(mob.wanderAngle), sinH = Math.sin(mob.wanderAngle)
        const aX = mob.nx + (ex * sinH + fx * cosH) * lookAhead
        const aY = mob.ny + (ey * sinH + fy * cosH) * lookAhead
        const aZ = mob.nz + (ez * sinH + fz * cosH) * lookAhead
        const len = Math.sqrt(aX * aX + aY * aY + aZ * aZ)
        if (heightAt(aX / len, aY / len, aZ / len) < 0.001) {
          mob.wanderAngle += Math.PI * 0.5
        }
      }

      mob.heading = mob.wanderAngle

      if (mob.stateTimer <= 0) {
        mob.state = AnimalState.Idle
        mob.stateTimer = 1 + quickRng(tickSeed + 6, mob.nz * 23456) * 2.5
      }
      break
    }

    case AnimalState.Eat: {
      mob.speed = 0
      mob.animSpeed = 0.3
      if (mob.stateTimer <= 0) {
        mob.state = AnimalState.Idle
        mob.stateTimer = 0.5 + quickRng(tickSeed + 7, mob.nx * 87654)
      }
      break
    }

    case AnimalState.Flee: {
      mob.speed = speeds.fast
      mob.animSpeed = 1.8
      mob.heading = mob.wanderAngle
      if (mob.stateTimer <= 0) {
        mob.state = AnimalState.Idle
        mob.stateTimer = 1 + quickRng(tickSeed + 8, mob.ny * 44321) * 2
      }
      break
    }

    case AnimalState.Hunt: {
      // Wolves chase toward the player
      mob.speed = WOLF_HUNT_SPEED
      mob.animSpeed = 1.6
      const { ex, ey, ez, fx, fy, fz } = getTangentFrame(mob.nx, mob.ny, mob.nz)
      const toPlayerX = cameraNx - mob.nx
      const toPlayerY = cameraNy - mob.ny
      const toPlayerZ = cameraNz - mob.nz
      const projE = toPlayerX * ex + toPlayerY * ey + toPlayerZ * ez
      const projF = toPlayerX * fx + toPlayerY * fy + toPlayerZ * fz
      const huntAngle = Math.atan2(projE, projF)
      mob.heading = lerpAngle(mob.heading, huntAngle, 0.3)

      // Give up if too far or timer expired
      if (playerDist > WOLF_HUNT_GIVE_UP || mob.stateTimer <= 0) {
        mob.state = AnimalState.Wander
        mob.stateTimer = 3 + quickRng(tickSeed + 9, mob.nx * 66321) * 4
      }
      break
    }
  }
}

// ─── Move a mob on the sphere surface ───────────────────────────────
function moveMob(mob: MobData, dt: number) {
  if (mob.speed < 0.01) return

  const { ex, ey, ez, fx, fy, fz } = getTangentFrame(mob.nx, mob.ny, mob.nz)
  const cosH = Math.cos(mob.heading), sinH = Math.sin(mob.heading)
  const vx = sinH * mob.speed
  const vz = cosH * mob.speed

  const dx = (ex * vx + fx * vz) * dt
  const dy = (ey * vx + fy * vz) * dt
  const dz = (ez * vx + fz * vz) * dt

  const newX = mob.nx * mob.radius + dx
  const newY = mob.ny * mob.radius + dy
  const newZ = mob.nz * mob.radius + dz
  const len = Math.sqrt(newX * newX + newY * newY + newZ * newZ)
  mob.nx = newX / len
  mob.ny = newY / len
  mob.nz = newZ / len

  mob.animPhase = (mob.animPhase + mob.animSpeed * mob.speed * dt * 0.3) % 1
}

// ─── Game hour from elapsed time ────────────────────────────────────
function getGameHour(elapsedTime: number): number {
  return ((elapsedTime / SUN_CYCLE_DURATION) * 24 + 6) % 24
}

// ─── Main simulation ────────────────────────────────────────────────
export class MobSimulation {
  mobs: MobData[]
  villages: VillageSimData[]
  private spatialHash = new SphericalSpatialHash()
  private lastAiTick = 0
  private lastSpatialRebuild = 0
  private tickSeed = 0
  private frameIndex = 0

  // Camera position on unit sphere (for flee + LOD)
  cameraNx = 0; cameraNy = 1; cameraNz = 0

  constructor(mobs: MobData[], villages: VillageSimData[]) {
    this.mobs = mobs
    this.villages = villages
  }

  update(time: number, dt: number) {
    const clampedDt = Math.min(dt, 0.1)
    this.frameIndex++
    const gameHour = getGameHour(time)

    // Rebuild spatial hash at 2Hz
    if (time - this.lastSpatialRebuild > SPATIAL_REBUILD_INTERVAL) {
      this.spatialHash.rebuild(this.mobs)
      this.lastSpatialRebuild = time
    }

    // AI tick at 4Hz
    if (time - this.lastAiTick > AI_TICK_INTERVAL) {
      const aiDt = time - this.lastAiTick
      this.lastAiTick = time
      this.tickSeed++

      for (let i = 0; i < this.mobs.length; i++) {
        const mob = this.mobs[i]
        if (mob.type === MobType.Villager) {
          updateVillager(mob, gameHour, this.villages, this.tickSeed + i * 7)
        } else {
          animalAiTick(
            mob, aiDt, this.tickSeed + i * 7,
            this.mobs, this.spatialHash,
            this.cameraNx, this.cameraNy, this.cameraNz,
          )
        }
      }
    }

    // Movement at render rate
    for (let i = 0; i < this.mobs.length; i++) {
      moveMob(this.mobs[i], clampedDt)
    }

    // Staggered terrain height sampling
    const offset = this.frameIndex % HEIGHT_STAGGER
    for (let i = offset; i < this.mobs.length; i += HEIGHT_STAGGER) {
      const mob = this.mobs[i]
      mob.radius = sampleTerrainRadius(mob.nx, mob.ny, mob.nz)
    }
  }
}
